import React, { useMemo, useState } from 'react';
import CustomSelect from 'components/ui/customSelect';
import Input from 'components/ui/input';
import Button from 'components/ui/button';
import { RelativePeriod } from 'types/Analytics';
import { TIME_UNITS } from 'constants/dateFormats';
import './relativeDatePicker.scss';
import getLocalizedText from 'helpers/getLocalizedText';

export type RelativeUnit = 'hours' | 'days' | 'weeks' | 'months';

interface Props {
  value?: string; // например "-7 days"
  onChange: (period: RelativePeriod) => void;
  maxDays?: number; // ограничение в днях (7 при groupByDate='1h', 365 по умолчанию)
  disabled?: boolean;
}

/** Парсит строку вида "-7 days" → { n: 7, unit: 'days' } */
const parseRelative = (value?: string): { n: number; unit: RelativeUnit } | null => {
  if (!value) return null;
  const match = value.match(/^-(\d+) (hour|day|week|month)s?$/);
  if (!match) return null;
  const unitMap: Record<string, RelativeUnit> = {
    hour: 'hours',
    day: 'days',
    week: 'weeks',
    month: 'months',
  };
  return { n: Number(match[1]), unit: unitMap[match[2]] };
};

/** Сериализует обратно в строку для бэкенда: "-7 days" */
const serializeRelative = (n: number, unit: RelativeUnit): string => {
  const singular = unit.replace(/s$/, ''); // days → day
  return `-${n} ${singular}`;
};

const MAX_DAYS_DEFAULT = TIME_UNITS.DAYS_PER_YEAR;
const MAX_DAYS_HOURLY = TIME_UNITS.DAYS_PER_WEEK;

const maxValueForUnit = (unit: RelativeUnit, maxDays: number): number => {
  switch (unit) {
    case 'hours':  return MAX_DAYS_HOURLY * TIME_UNITS.HOURS_PER_DAY;
    case 'days':   return maxDays;
    case 'weeks':  return Math.floor(maxDays / TIME_UNITS.DAYS_PER_WEEK);
    case 'months': return Math.floor(maxDays / TIME_UNITS.APPROX_DAYS_PER_MONTH);
  }
};

const RelativeDatePicker: React.FC<Props> = ({
  value,
  onChange,
  maxDays = MAX_DAYS_DEFAULT,
  disabled = false,
}) => {
  const getTranslate = getLocalizedText;
  
  const UNIT_OPTIONS_LIMITED = useMemo(() => [
    { value: 'hours', label: getTranslate('analytics.editForm.params.period.relative.unit.hours') },
    { value: 'days',  label: getTranslate('analytics.editForm.params.period.relative.unit.days') },
  ], [getTranslate]);

  const  UNIT_OPTIONS_FULL = useMemo(() => [
    { value: 'hours', label: getTranslate('analytics.editForm.params.groupPeriodBy.hour.item') },
    { value: 'days',  label: getTranslate('analytics.editForm.params.groupPeriodBy.day.item') },
    { value: 'weeks',  label: getTranslate('analytics.editForm.params.groupPeriodBy.week.item') },
    { value: 'months', label: getTranslate('analytics.editForm.params.groupPeriodBy.month.item') },
  ], [getTranslate]);

  const isLimited = maxDays <= MAX_DAYS_HOURLY;
  const unitOptions = isLimited ? UNIT_OPTIONS_LIMITED : UNIT_OPTIONS_FULL;
  const parsed = parseRelative(value);

  // Если сохранённый юнит недоступен при текущем ограничении — откатываемся к 'days'
  const resolvedUnit = (): RelativeUnit => {
    const u = parsed?.unit ?? 'days';
    if (isLimited && (u === 'weeks' || u === 'months')) return 'days';
    return u;
  };

  const [currentUnit, setCurrentUnit] = useState<RelativeUnit>(resolvedUnit);
  const [inputValue, setInputValue]   = useState<string>(String(parsed?.n ?? 1));
  const [inputError, setInputError]   = useState<string>('');

  const max = maxValueForUnit(currentUnit, maxDays);

  const hintText = useMemo(() => {
    if (currentUnit === 'hours') {
      return `${getTranslate('analytics.editForm.params.period.relative.hint.max')} ${MAX_DAYS_HOURLY} ${getTranslate('analytics.editForm.params.period.relative.unit.days')}`;
    }

    return `${getTranslate('analytics.editForm.params.period.relative.hint.max')} ${max} ${getTranslate(`analytics.editForm.params.period.relative.unit.${currentUnit}`)}`;
  }, [currentUnit, max]);

  const validate = (raw: string): number | null => {
    const n = parseInt(raw, 10);
    if (isNaN(n) || n < 1 || n > max) return null;
    return n;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, ''); // только цифры
    setInputValue(raw);

    if (raw === '') {
      setInputError('');
      return;
    }

    const n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) {
      setInputError(`${getTranslate('analytics.editForm.params.period.relative.validation.min')} 1`);
    } else if (n > max) {
      setInputError(`${getTranslate('analytics.editForm.params.period.relative.validation.max')} ${max}`);
    } else {
      setInputError('');
    }
  };

  const handleInputBlur = () => {
    // При потере фокуса — корректируем значение к допустимому диапазону
    const n = parseInt(inputValue, 10);
    if (isNaN(n) || n < 1) {
      setInputValue('1');
      setInputError('');
    } else if (n > max) {
      setInputValue(String(max));
      setInputError('');
    }
  };

  const handleUnitChange = ({ value: newUnit }: { value: string }) => {
    const unit = newUnit as RelativeUnit;
    const newMax = maxValueForUnit(unit, maxDays);
    const currentNum = parseInt(inputValue, 10) || 1;

    setCurrentUnit(unit);
    setInputError('');

    // Clamp текущего числа к новому максимуму
    if (currentNum > newMax) {
      setInputValue(String(newMax));
    }
  };

  const handleApply = () => {
    const n = validate(inputValue);
    if (n === null) return;
    onChange({ relative: serializeRelative(n, currentUnit) });
  };

  const selectedUnit = unitOptions.find((o) => o.value === currentUnit) ?? unitOptions[0];
  const isApplyDisabled = validate(inputValue) === null;

  return (
    <div className='relative-date-picker'>
      <div className='relative-date-picker__main'>
        <span className='relative-date-picker__label'>{getTranslate('analytics.editForm.params.period.relative.label.last')}</span>

        <div className='relative-date-picker__input-wrap'>
          <Input
            id='relative-date-picker-number'
            customClass='relative-date-picker__input'
            value={inputValue}
            placeholder='1'
            disabled={disabled}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            type='text'
          />
          {inputError ? (
            <span className='relative-date-picker__hint relative-date-picker__hint_error'>
              {inputError}
            </span>
          ) : (
            <span className='relative-date-picker__hint'>
              {hintText}
            </span>
          )}
        </div>

        <CustomSelect
          id='relative-date-picker-unit'
          customClass='relative-date-picker__select relative-date-picker__select_unit'
          options={unitOptions}
          value={selectedUnit}
          isDisabled={disabled}
          onChange={handleUnitChange}
        />

        <span className='relative-date-picker__label'>{getTranslate('analytics.editForm.params.period.relative.label.toNow')}</span>
      </div>

      <div className='relative-date-picker__footer'>
        {!disabled && (
          <div className='relative-date-picker__footer-item_button'>
            <Button
              onClick={handleApply}
              disabled={isApplyDisabled}
              status='primary'
              id='apply-quick-dates'
              text={getTranslate('ui.datePicker.apply')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { parseRelative, serializeRelative, MAX_DAYS_DEFAULT, MAX_DAYS_HOURLY };
export default RelativeDatePicker;
